/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import {
  Editor,
  EditorState,
  RichUtils,
  CompositeDecorator,
  convertToRaw,
  convertFromRaw,
  ContentState,
  Modifier,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { LinkEditorModal } from "../../../pages-admin";
import { Button } from "../../../ui";
import { PageTheme } from "../../../pages";

type Props = {
  raw: string;
  onFinish: (html: string, raw: any) => void;
  contentId?: string; // To let the editor know when the content has changed and editor state should be updated
};

const Link = (props) => {
  const { url, buttonLink, linkColor } = props.contentState
    .getEntity(props.entityKey)
    .getData();
  let className = "";
  if (buttonLink) {
    className = "btn " + PageTheme.getThemeColorButtonClass(linkColor);
  } else {
    className = PageTheme.getThemeColorTextClass(linkColor);
  }
  return (
    <a className={className} href={url}>
      {props.children}
    </a>
  );
};

function findLinkEntities(contentBlock: any, callback: any, contentState: any) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
}

const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

type InlineStyle = {
  offset: number;
  style: string;
  length: number;
};

type Entity = {
  data: any;
  mutability: string;
  type: string;
};

type EntityRange = {
  key: number;
  offset: number;
  length: number;
};

type Block = {
  key: string;
  text: string;
  type: string;
  depth: number;
  inlineStyleRanges: InlineStyle[];
  entityRanges: [];
  data: any;
};

const alignmentStyles = ["LEFT", "RIGHT", "CENTER"];

/**
 * Calculates a new offset map
 * @param offsetMap The original offset map
 * @param index The index where a text is inserted
 * @param length The length of the inserted text
 */
const updateOffsetMap = (
  offsetMap: number[],
  index: number,
  length: number,
) => {
  const newOffsetMap: number[] = [];
  for (let i = 0; i < offsetMap.length; i++) {
    if (i < index) newOffsetMap.push(offsetMap[i]);
    else newOffsetMap.push(offsetMap[i] + length);
  }
  return newOffsetMap;
};

const getAdjustedOffset = (originalOffset: number, offsetMap: number[]) => {
  if (originalOffset === offsetMap.length) {
    return offsetMap[offsetMap.length - 1] + 1;
  }
  if (originalOffset > offsetMap.length) {
    console.error(originalOffset, offsetMap);
    throw new Error("Offset out of range");
  }
  return offsetMap[originalOffset];
};

const insertText = (
  originalText: string,
  insertedText: string,
  position: number,
) => {
  // In this case inserted text is jusst appended
  if (position === originalText.length) return originalText + insertedText;
  return (
    originalText.slice(0, position) +
    insertedText +
    originalText.slice(position)
  );
};

const convertToHtml = (blocks: Block[], entities: Entity[]) => {
  let html = "";
  let parsingUnorderedList = false;
  let parsingOrderedList = false;

  blocks.forEach((block: Block) => {
    const text = block.text;
    let styledText = text;
    let offsetMap: number[] = [];
    for (let i = 0; i < text.length; i++) {
      offsetMap.push(i);
    }

    // Close previous list
    if (parsingUnorderedList && block.type !== "unordered-list-item") {
      html += "</ul>";
      parsingUnorderedList = false;
    }
    if (parsingOrderedList && block.type !== "ordered-list-item") {
      html += "</ol>";
      parsingOrderedList = false;
    }

    let alignment = "LEFT";
    // Parse styles (bold, italic)
    block.inlineStyleRanges.forEach((style: InlineStyle) => {
      // styledText += text.substring(index, getAdjustedOffset(style.offset, offsetMap));
      if (style.style === "BOLD") {
        // styledText += '<span class="fw-bold">';
        styledText = insertText(
          styledText,
          '<span class="text-bold">',
          getAdjustedOffset(style.offset, offsetMap),
        );
        offsetMap = updateOffsetMap(offsetMap, style.offset, 24);
      } else if (style.style === "ITALIC") {
        // styledText += '<span class="fst-italic">';
        styledText = insertText(
          styledText,
          '<span class="text-italic">',
          getAdjustedOffset(style.offset, offsetMap),
        );
        offsetMap = updateOffsetMap(offsetMap, style.offset, 26);
      } else if (alignmentStyles.includes(style.style)) {
        // Alignment is set on block level
        alignment = style.style;
      }
      // styledText += text.substr(getAdjustedOffset(style.offset, offsetMap), style.length);
      // styledText += "</span>";
      // index = style.offset + style.length;
      // offsetMap = updateOffsetMap(offsetMap, index, 7);
      styledText = insertText(
        styledText,
        "</span>",
        getAdjustedOffset(style.offset + style.length, offsetMap),
      );
      offsetMap = updateOffsetMap(offsetMap, style.offset + style.length, 7);
      // Add the rest of the text for the last one
      // if (styleIndex === block.inlineStyleRanges.length - 1) {
      //   styledText += text.substring(style.offset + style.length);
      // }
    });
    // if (block.inlineStyleRanges.length === 0) styledText = block.text;
    // Parse links
    block.entityRanges.forEach((entityRange: EntityRange) => {
      const key = entityRange.key;
      if (entities[key] !== undefined) {
        // Link / button link styling
        if (
          entities[key].type === "LINK" &&
          entities[key].data.url !== undefined
        ) {
          const url = entities[key].data.url;
          let className = "";

          if (entities[key].data.buttonLink === true) {
            className +=
              " btn " +
              PageTheme.getThemeColorButtonClass(entities[key].data.linkColor);
          } else if (entities[key].data.linkColor !== undefined) {
            className += PageTheme.getThemeColorTextClass(
              entities[key].data.linkColor,
            );
          }
          const insert = '<a href="' + url + '" class="' + className + '">';
          styledText = insertText(
            styledText,
            insert,
            getAdjustedOffset(entityRange.offset, offsetMap),
          );
          offsetMap = updateOffsetMap(
            offsetMap,
            entityRange.offset,
            insert.length,
          );
          styledText = insertText(
            styledText,
            "</a>",
            getAdjustedOffset(
              entityRange.offset + entityRange.length,
              offsetMap,
            ),
          );
          offsetMap = updateOffsetMap(
            offsetMap,
            entityRange.offset + entityRange.length,
            4,
          );
        }
      }
    });

    // Set alignment
    let blockClass = "";
    if (alignment === "CENTER") {
      blockClass = " text-center";
    } else if (alignment === "RIGHT") {
      blockClass = " text-right";
    }

    if (block.type === "unstyled" || block.type === "paragraph")
      html +=
        '<p class="' +
        blockClass +
        '">' +
        styledText.replace(/\n/, "</br>") +
        "</p>";
    else if (block.type === "header-one")
      html += '<h1 class="' + blockClass + '">' + styledText + "</h1>";
    else if (block.type === "header-two")
      html += '<h2 class="' + blockClass + '">' + styledText + "</h2>";
    else if (block.type === "header-three")
      html += '<h3 class="' + blockClass + '">' + styledText + "</h3>";
    else if (block.type === "header-four")
      html += '<h4 class="' + blockClass + '">' + styledText + "</h4>";
    else if (block.type === "header-five")
      html += '<h5 class="' + blockClass + '">' + styledText + "</h5>";
    else if (block.type === "header-six")
      html += '<h6 class="' + blockClass + '">' + styledText + "</h6>";
    else if (block.type === "unordered-list-item") {
      if (!parsingUnorderedList) html += '<ul class="' + blockClass + '">';
      parsingUnorderedList = true;
      html += "<li>" + styledText + "</li>";
    } else if (block.type === "ordered-list-item") {
      if (!parsingOrderedList) html += '<ol class="' + blockClass + '">';
      parsingOrderedList = true;
      html += "<li>" + styledText + "</li>";
    }
  });
  // Close if content ended with a list item
  if (parsingUnorderedList) {
    html += "</ul>";
  }
  if (parsingOrderedList) {
    html += "</ol>";
  }
  return html;
};

/**
 * Checks if the selected text is a link. If it is, the link is returned. Otherwise null is returned.
 * @param editorState
 * @returns
 */
function getSelectedLink(
  editorState: EditorState,
): { url: string; linkColor: number; buttonLink: boolean } | null {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();
  let link = null;

  if (startKey !== endKey) return null;

  const block = contentState.getBlockForKey(startKey);
  block.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === "LINK"
      );
    },
    (start) => {
      const entityKey = block.getEntityAt(start);
      link = contentState.getEntity(entityKey).getData();
    },
  );

  return link;
}

const getInitialContent = (raw: any) => {
  let contentState: any;
  if (raw !== undefined) {
    return convertFromRaw(raw);
    // const {contentBlocks, entityMap} = blocksFromHtml;
    // contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  } else {
    return ContentState.createFromText("");
  }

  return contentState;
  // const editorState = EditorState.createWithContent(contentState);
  // return editorState;
};

/**
 * Display an editor for the text content.
 */
export const RichTextEditor = (props: Props) => {
  const [editorState, setEditorState] = useState<EditorState>(() =>
    EditorState.createWithContent(getInitialContent(props.raw), decorator),
  );
  const [showLinkModal, setShowLinkModal] = useState(false);

  // Link editor state
  const [linkUrl, setLinkUrl] = useState("");
  const [buttonLink, setButtonLink] = useState(false);
  const [linkColor, setLinkColor] = useState(0);

  useEffect(() => {
    setEditorState(
      EditorState.createWithContent(getInitialContent(props.raw), decorator),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contentId]);

  const toRaw = (state: EditorState) => {
    const contentState = state.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    return rawContent;
  };

  const toHtml = (rawContent: any) => {
    // const contentState = editorState.getCurrentContent();
    // const rawContent = convertToRaw(contentState);
    return convertToHtml(
      rawContent.blocks as Block[],
      rawContent.entityMap as Entity[],
    );
  };

  const finishEditing = () => {
    const raw = toRaw(editorState);
    const html = toHtml(raw);
    props.onFinish(html, raw);
  };

  const setEditorStateDirectly = (newEditorState: EditorState) => {
    // const raw = toRaw(newEditorState);
    setEditorState(newEditorState);
  };

  const updateEditorState = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const raw = toRaw(newEditorState);
    const html = toHtml(raw);
    props.onFinish(html, raw);
  };

  const isActive = (style: string) => {
    const hasStyle = editorState.getCurrentInlineStyle().has(style);
    if (hasStyle) return true;
    const currentType = RichUtils.getCurrentBlockType(editorState);
    if (currentType === style) return true;
    return false;
    // const currentStyles = editorState.getCurrentInlineStyle();
    // return hasStyle;
  };

  const onBoldClick = () => {
    updateEditorState(RichUtils.toggleInlineStyle(editorState, "BOLD"));
  };

  const onItalicClick = () => {
    updateEditorState(RichUtils.toggleInlineStyle(editorState, "ITALIC"));
  };

  const prepareLink = () => {
    const link = getSelectedLink(editorState);
    if (link !== null) {
      setLinkUrl(link.url);
      setButtonLink(link.buttonLink);
      setLinkColor(link.linkColor);
    } else {
      setLinkColor(0);
      setButtonLink(false);
      setLinkUrl("");
    }
    setShowLinkModal(true);
  };

  const createLink = (url: string, color: number, buttonLink: boolean) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "LINK",
      "MUTABLE",
      {
        url: url,
        buttonLink: buttonLink,
        linkColor: color,
      },
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });
    updateEditorState(
      RichUtils.toggleLink(
        newEditorState,
        newEditorState.getSelection(),
        entityKey,
      ),
    );
    setShowLinkModal(false);
  };

  // const removeLink = () => {
  //   // e.preventDefault();
  //   // const {editorState} = this.state;
  //   const selection = editorState.getSelection();
  //   if (!selection.isCollapsed()) {
  //     updateEditorState(RichUtils.toggleLink(editorState, selection, null));
  //   }
  // };

  const toggleBlockType = (type: string) => {
    const currentType = RichUtils.getCurrentBlockType(editorState);
    if (currentType === "paragraph" || currentType === "unstyled") {
      updateEditorState(RichUtils.toggleBlockType(editorState, type));
    } else {
      updateEditorState(RichUtils.toggleBlockType(editorState, "paragraph"));
    }
  };

  const toggleHeader = (level: number) => {
    let type = "header-";
    switch (level) {
      case 1:
        type += "one";
        break;
      case 2:
        type += "two";
        break;
      case 3:
        type += "three";
        break;
      case 4:
        type += "four";
        break;
      case 5:
        type += "five";
        break;
      case 6:
        type += "six";
        break;
    }
    toggleBlockType(type);
    // updateEditorState(RichUtils.toggleBlockType(editorState, type));
  };

  const toggleUnorderedList = () => {
    updateEditorState(
      RichUtils.toggleBlockType(editorState, "unordered-list-item"),
    );
  };

  // const toggleOrderedList = () => {
  //   updateEditorState(
  //     RichUtils.toggleBlockType(editorState, "ordered-list-item"),
  //   );
  // };

  const toggleParagraph = () => {
    toggleBlockType("paragraph");
    // updateEditorState(RichUtils.toggleBlockType(editorState, "paragraph"));
  };

  const setAlignment = (newStyle: string) => {
    // const currentStyles = editorState.getCurrentInlineStyle();

    const currentContent = editorState.getCurrentContent();

    // Make sure the entire block get the alignment style
    const selection = editorState.getSelection();
    const focusBlock = currentContent.getBlockForKey(selection.getFocusKey());
    const anchorBlock = currentContent.getBlockForKey(selection.getAnchorKey());
    const isBackward = selection.getIsBackward();

    const selectionMerge = {
      anchorOffset: 0,
      focusOffset: focusBlock.getLength(),
    };

    if (isBackward) {
      selectionMerge.anchorOffset = anchorBlock.getLength();
    }
    const finalSelection = selection.merge(selectionMerge);

    // Remove any existing alignment styles

    let newContent = currentContent;
    alignmentStyles.forEach((style: any) => {
      if (style !== newStyle) {
        // newState = RichUtils.toggleInlineStyle(newState, style);
        newContent = Modifier.removeInlineStyle(
          newContent,
          finalSelection,
          style,
        );
      }
    });

    newContent = Modifier.applyInlineStyle(
      newContent,
      finalSelection,
      newStyle,
    );
    const newState = EditorState.push(
      editorState,
      newContent,
      "change-inline-style",
    );
    updateEditorState(newState);
  };

  const handleReturn = (e: any) => {
    // const { editorState } = this.state;
    if (e.shiftKey) {
      updateEditorState(RichUtils.insertSoftNewline(editorState));
      return "handled";
    }
    return "not-handled";
  };

  const myBlockStyleFn = (contentBlock: any) => {
    // const type = contentBlock.getType();
    // if (type === "unstyled" || type === "paragraph") {
    let className = "paragraph-spacing";

    let alignment = " text-left";
    contentBlock.findStyleRanges((e) => {
      if (e.hasStyle("CENTER")) {
        alignment = " text-center";
      }
      if (e.hasStyle("RIGHT")) {
        alignment = " text-right";
      }
    });
    className += alignment;
    return className;
    // }
  };

  // useEffect(() => {
  //   if (props.trigger) {
  //     const raw = toRaw();
  //     const html = toHtml(raw);
  //     props.onFinish(html, raw);
  //   }
  // }, [props.trigger]);

  return (
    <>
      <div className="rich-text-editor">
        <div className="toolbar">
          <Button
            active={isActive("paragraph")}
            onClick={toggleParagraph}
            transparent
            icon="p"
          />

          <Button
            active={isActive("header-one")}
            onClick={() => toggleHeader(1)}
            transparent
            icon="h1"
          />
          <Button
            active={isActive("header-two")}
            onClick={() => toggleHeader(2)}
            transparent
            icon="h2"
          />
          <Button
            active={isActive("header-three")}
            onClick={() => toggleHeader(3)}
            transparent
            icon="h3"
          />
          <Button
            active={isActive("header-four")}
            onClick={() => toggleHeader(4)}
            transparent
            icon="h4"
          />
          <Button
            active={isActive("header-five")}
            onClick={() => toggleHeader(5)}
            transparent
            icon="h5"
          />
          <Button
            active={isActive("header-six")}
            onClick={() => toggleHeader(6)}
            transparent
            icon="h6"
          />

          <Button
            active={isActive("BOLD")}
            transparent
            onClick={onBoldClick}
            icon="bold"
          />
          <Button
            active={isActive("ITALIC")}
            onClick={onItalicClick}
            transparent
            icon="italic"
          />
          <Button
            active={isActive("LINK")}
            onClick={prepareLink}
            transparent
            icon="link"
          />
          <Button
            active={isActive("unordered-list-item")}
            onClick={toggleUnorderedList}
            transparent
            icon="list-unordered"
          />
          {/* <Button
            className={isActive("ordered-list-item") ? " active" : ""}
            onClick={toggleOrderedList}
          >
            <BsListOl />
          </Button> */}
          <Button
            active={isActive("LEFT")}
            onClick={() => setAlignment("LEFT")}
            transparent
            icon="align-left"
          />
          <Button
            active={isActive("CENTER")}
            onClick={() => setAlignment("CENTER")}
            transparent
            icon="align-center"
          />
          <Button
            active={isActive("RIGHT")}
            onClick={() => setAlignment("RIGHT")}
            transparent
            icon="align-right"
          />
        </div>
        <Editor
          editorState={editorState}
          onChange={setEditorStateDirectly}
          handleReturn={handleReturn}
          blockStyleFn={myBlockStyleFn}
          onBlur={finishEditing}
        />
      </div>
      {/* Link editor */}
      {showLinkModal && (
        <LinkEditorModal
          onClose={() => setShowLinkModal(false)}
          onInsert={createLink}
          url={linkUrl}
          color={linkColor}
          buttonLink={buttonLink}
        />
      )}
    </>
  );
};

export default RichTextEditor;
